import React, { FC, useContext, useState, useMemo, useCallback } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { MessageMap } from 'ia-react-core/~/interfaces/MessageMap';
import { MessageType } from 'ia-react-core/~/interfaces/MessageType';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';

import ValidationContext from '../../../components/form/#ValidationContext';

export interface WithValidationContextProps {

}

const withValidationContext = (WrappedComponent: FC<WithValidationContextProps>) => {
  const WithValidationContext = (props: WithValidationContextProps) => {
    const { messages: parentMessages = {} } = useContext(ValidationContext) || {};
    const [messages, setMessages] = useState([]);

    const messagesAsObject = useMemo(() => messages.reduce((currentMessages, { type, message }) => {
      currentMessages[type] = message;
      return currentMessages;
    }, {}), [messages]);

    const contextMessages = useMemo<MessageMap>(() => ({
      ...parentMessages,
      ...messagesAsObject,
    }), [messagesAsObject, parentMessages]);

    const registerMessage = useCallback((validationMessageId: number, type: string, message: MessageType<any>) => {
      setMessages((currentMessages) => [...currentMessages, { validationMessageId, type, message }]);
    }, []);

    const unregisterMessage = useCallback((validationMessageId: number) => {
      setMessages((currentMessages) => {
        const messagesCopy = currentMessages.concat();
        const index = messagesCopy.findIndex((message) => message.validationMessageId === validationMessageId);
        messagesCopy.splice(index, 1);
        return messagesCopy;
      });
    }, []);

    const validationContext = useMemo(() => ({
      messages: contextMessages,
      registerMessage,
      unregisterMessage,
    }), [contextMessages, registerMessage, unregisterMessage]);

    return (
      <ValidationContext.Provider
        value={validationContext}
      >
        <WrappedComponent {...props} />
      </ValidationContext.Provider>
    );
  };

  WithValidationContext.displayName = getWrappedDisplayName('WithValidationContext', WrappedComponent);

  return hoistNonReactStatics(WithValidationContext, WrappedComponent);
};

export default withValidationContext;
